import { diff3Merge } from 'node-diff3';
import { HttpErrorResponse } from '@angular/common/http';

export type ItemWriteConflict =
  | { kind: 'text'; current: string }
  | { kind: 'payload'; currentPayload: unknown };

export function readItemWriteConflict(
  error: unknown
): ItemWriteConflict | null {
  if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
    return null;
  }
  const body = error.error as Record<string, unknown> | string | null;
  if (!body || typeof body !== 'object') return null;
  if (typeof body['current'] === 'string') {
    return { kind: 'text', current: body['current'] };
  }
  if ('currentPayload' in body) {
    return { kind: 'payload', currentPayload: body['currentPayload'] };
  }
  return null;
}

export interface MergedText {
  text: string;
  overlapped: boolean;
  discarded: string[];
}

// Words, whitespace runs or single punctuation marks, so editing one word leaves its neighbours alone.
const TOKEN = /\s+|[\p{L}\p{N}_]+|[^\s\p{L}\p{N}_]/gu;

interface ConflictedSpan {
  original: string;
  server: string;
  local: string;
}

export function mergeTexts(
  base: string,
  server: string,
  local: string,
  multiline: boolean
): MergedText {
  const merged: MergedText = { text: '', overlapped: false, discarded: [] };
  const regions = diff3Merge<string>(
    tokens(server),
    tokens(base),
    tokens(local),
    { excludeFalseConflicts: true }
  );
  for (const region of regions) {
    if (region.ok) {
      merged.text += region.ok.join('');
      continue;
    }
    const { a, o, b } = region.conflict!;
    const span = {
      original: o.join(''),
      server: a.join(''),
      local: b.join(''),
    };
    merged.text += mergeConflictedSpan(span, multiline, merged);
  }
  return merged;
}

function mergeConflictedSpan(
  span: ConflictedSpan,
  multiline: boolean,
  merged: MergedText
): string {
  const combined = combineEditWithAdjacentInsertion(span);
  if (combined !== null) return combined;
  if (isNumber(span.server) && isNumber(span.local)) {
    merged.discarded.push(span.local.trim());
    return span.server;
  }
  merged.overlapped = true;
  return keepBothSpans(span.server, span.local, multiline);
}

function isNumber(span: string): boolean {
  return /^\s*[-+]?\d+(?:[.,]\d+)?\s*$/.test(span);
}

function tokens(text: string): string[] {
  return text.match(TOKEN) ?? [];
}

// diff3 reports an edit of a span and an insertion right next to it as one conflict, and applying
// the edit and keeping the insertion loses neither.
function combineEditWithAdjacentInsertion({
  original,
  server,
  local,
}: ConflictedSpan): string | null {
  if (original === '') return null;
  return (
    withInsertionAfter(original, server, local) ??
    withInsertionAfter(original, local, server) ??
    withInsertionBefore(original, server, local) ??
    withInsertionBefore(original, local, server)
  );
}

function withInsertionAfter(
  original: string,
  inserted: string,
  edited: string
): string | null {
  if (!inserted.startsWith(original) || edited.startsWith(original)) {
    return null;
  }
  return edited + inserted.slice(original.length);
}

function withInsertionBefore(
  original: string,
  inserted: string,
  edited: string
): string | null {
  if (!inserted.endsWith(original) || edited.endsWith(original)) {
    return null;
  }
  return inserted.slice(0, -original.length) + edited;
}

function keepBothSpans(
  server: string,
  local: string,
  multiline: boolean
): string {
  if (local.includes(server)) return local;
  if (server.includes(local)) return server;
  return server + separatorBetween(server, local, multiline) + local;
}

function separatorBetween(
  server: string,
  local: string,
  multiline: boolean
): string {
  if (/\s$/.test(server) || /^\s/.test(local)) return '';
  const breaks = multiline && (server.includes('\n') || local.includes('\n'));
  return breaks ? '\n' : ' ';
}

export function payloadValue(payload: unknown): unknown {
  if (payload && typeof payload === 'object' && 'value' in payload) {
    return (payload as { value: unknown }).value;
  }
  return null;
}
